import { type ReactNode } from "react";
import { type AliasesProps, getAliases, ProductAliases } from "./ProductAliases";
import { isKITKITProduct } from "../../../models/ProductData";
import { userIsConsumerDistributor, userIsDistributor } from "../../../utils/roles";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import type { WithT } from "i18next";
import {
  Parameter,
  ParameterStack,
  type ParameterProps,
} from "~/Search/components/ProductResults/views/ListView/components/SearchResultListItemNew/Parameter";

type ProductParamContext = {
  t: WithT["t"];
  product: ProductDescriptor;
};

export const ProductParams2 = {
  OldDesignation: ({ product, t }: ProductParamContext) => {
    if (!product.base.oldDesignation || isKITKITProduct(product)) {
      return null;
    }

    return {
      label: t("product.oldDesignations"),
      value: product.base.oldDesignation,
    };
  },
  SchaefflerId: ({ product, t }: ProductParamContext) => {
    if (!product.base.sapMaterialNumber || isKITKITProduct(product)) {
      return null;
    }

    return {
      label: t("product.schaefflerId"),
      value: product.base.sapMaterialNumber,
    };
  },
  GPSD: ({ product, t }: ProductParamContext) => {
    if (!product.base.gpsdGroup || isKITKITProduct(product)) {
      return null;
    }
    if (!userIsDistributor() && !userIsConsumerDistributor()) {
      return null;
    }

    return {
      label: t("common.product.gpsdGroup"),
      value: product.base.gpsdGroup,
    };
  },
  ProductAliases:
    (config: Omit<AliasesProps, "product">) =>
    ({ product, t }: ProductParamContext) => {
      if (isKITKITProduct(product)) {
        return null;
      }

      if (getAliases(product.base, config.mode ?? "all", config.packCode).length === 0) {
        return null;
      }

      return {
        label: t("product.customerId"),
        value: <ProductAliases product={product.base} {...config} />,
      };
    },
} as const;

type Param = {
  label: ReactNode;
  value: ReactNode;
};
interface ParametersProps {
  data: Param[];
  showEmpty?: boolean;
  emptySymbol?: ReactNode;
  parameterVariant?: ParameterProps["variant"];
}

export const Parameters = ({ data, showEmpty = false, emptySymbol = "-", parameterVariant }: ParametersProps) => {
  if (!showEmpty) {
    data = data.filter((param) => !!param.value);
  }
  if (data.length === 0) {
    return null;
  }
  return (
    <ParameterStack parameterVariant={parameterVariant}>
      {data.map((param, index) => (
        <Parameter key={index} label={param.label} value={param.value ?? emptySymbol} />
      ))}
    </ParameterStack>
  );
};
